import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import {
  TestSessionStatus,
  useTestSessionsData,
  useTestsData,
  useAuth,
  UserPermissions,
  GroupMemberRole,
  CompanyMemberRole,
} from '@modmd/data'
import { TestsDatalist } from 'sharedComponents/TestsDatalist'
import { TestSessionsDatalist } from 'sharedComponents/TestSessionsDatalist'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { Box, Grid } from 'components/Layout'
import { ROUTES } from 'internal-portal/constants/routes'
import Datalist from 'components/Datalist'
import Button from 'components/Button'
import { useSortChangeHandler } from 'utils/helpers'

const Dashboard = ({ history }: RouteComponentProps) => {
  const { getUserCompaniesWithRole, getUserGroupsWithRole, hasPermission, data } = useAuth()
  const userId =
    hasPermission([UserPermissions.SEE_STAFFED_TEST_SESSION]) &&
    !hasPermission([UserPermissions.SEE_FINISHED_TEST_SESSIONS]) &&
    !hasPermission([UserPermissions.SCHEDULE_TEST_SESSION_ASSIGNED_GROUP_COMPANY_USERS]) &&
    data.User.id

  const commonFilter = React.useMemo(
    () =>
      userId &&
      !hasPermission([UserPermissions.SEE_FINISHED_TEST_SESSIONS]) &&
      !hasPermission([UserPermissions.SCHEDULE_TEST_SESSION_ASSIGNED_GROUP_COMPANY_USERS])
        ? { userId, isStaff: true }
        : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId]
  )

  const { companies } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner, CompanyMemberRole.Employee],
  })
  const { groups } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Coordinator, GroupMemberRole.Manager, GroupMemberRole.Member],
  })

  const getCompanyIds = () => {
    if (
      !hasPermission([UserPermissions.SEE_FULL_TEST_SESSIONS]) &&
      hasPermission([UserPermissions.SEE_GROUP_COMPANY_DASHBOARD])
    )
      return companies.length ? companies.map(({ id }) => id) : undefined
    return undefined
  }

  const getGroupIds = () => {
    if (
      !companies.length &&
      !hasPermission([UserPermissions.SEE_FULL_TEST_SESSIONS]) &&
      hasPermission([UserPermissions.SEE_GROUP_COMPANY_DASHBOARD])
    )
      return groups.length ? groups.map(({ id }) => id) : undefined
    return undefined
  }

  const {
    sorter: requestedSessionSorter,
    handleSortChange: handleSortChangeRequested,
  } = useSortChangeHandler({
    isInSearchState: false,
  })
  const {
    sorter: scheduledSessionSorter,
    handleSortChange: handleSortChangeScheduled,
  } = useSortChangeHandler({
    isInSearchState: false,
  })
  const requestedTestSessionsQuery = useTestSessionsData({
    filter: {
      statuses: [TestSessionStatus.Requested],
      companyIds: getCompanyIds(),
      groupIds: getGroupIds(),
      ...commonFilter,
    },
    sorter: requestedSessionSorter,
    skip: !hasPermission([UserPermissions.SEE_REQUESTED_TEST_SESSIONS_DASHBOARD]),
  })
  const scheduledTestSessionsQuery = useTestSessionsData({
    filter: {
      statuses: [TestSessionStatus.Scheduled],
      companyIds: getCompanyIds(),
      groupIds: getGroupIds(),
      ...commonFilter,
    },
    sorter: scheduledSessionSorter,
    skip: !hasPermission([UserPermissions.SEE_SCHEDULED_TEST_SESSIONS_DASHBOARD]),
  })
  const pendingResultsTestSessionsQuery = useTestSessionsData({
    filter: {
      statuses: [TestSessionStatus.PendingResults],
      ...commonFilter,
      companyIds: getCompanyIds(),
      groupIds: getGroupIds(),
    },
    skip: !hasPermission([UserPermissions.SEE_PENDING_RESULTS_TEST_SESSIONS_DASHBOARD]),
  })

  const testsQuery = useTestsData({
    filter: {
      hasResult: true,
      isProvisioned: false,
      companyIds: getCompanyIds(),
      groupIds: getGroupIds(),
    },
    skip: !!userId || !hasPermission([UserPermissions.SEE_FOLLOWUP_TEST_RESULT_DASHBOARD]),
  })

  return (
    <DashboardPage>
      <Grid display="grid" gridGap="1rem" gridTemplateColumns="1fr">
        {hasPermission([UserPermissions.SEE_REQUESTED_TEST_SESSIONS_DASHBOARD]) && (
          <Box minWidth={0}>
            <TestSessionsDatalist
              tableHeader={
                <Datalist.Title
                  rightContent={
                    <Button
                      size="medium"
                      colorVariant="secondary"
                      onClick={() => {
                        history.push({
                          pathname: `/${ROUTES.SESSIONS}`,
                          search: `?status=${TestSessionStatus.Requested}`,
                        })
                      }}
                    >
                      Show All
                    </Button>
                  }
                >
                  Requested Test Sessions
                </Datalist.Title>
              }
              sorter={requestedSessionSorter}
              onSortChange={handleSortChangeRequested}
              testSessions={requestedTestSessionsQuery.data?.testSessions?.testSessions || []}
              pagination={requestedTestSessionsQuery.data?.testSessions?.pagination}
              columnLabels={{
                status: null,
                invoiceCheck: null,
              }}
              isLoading={requestedTestSessionsQuery.loading}
              onRowClick={({ id }) => history.push(`/${ROUTES.SESSIONS}/${id}`)}
            />
          </Box>
        )}
        {hasPermission([UserPermissions.SEE_SCHEDULED_TEST_SESSIONS_DASHBOARD]) && (
          <Box minWidth={0}>
            <TestSessionsDatalist
              tableHeader={
                <Datalist.Title
                  rightContent={
                    <Button
                      size="medium"
                      colorVariant="secondary"
                      onClick={() => {
                        history.push({
                          pathname: `/${ROUTES.SESSIONS}`,
                          search: `?status=${TestSessionStatus.Scheduled}`,
                        })
                      }}
                    >
                      Show All
                    </Button>
                  }
                >
                  Scheduled Test Sessions
                </Datalist.Title>
              }
              sorter={scheduledSessionSorter}
              onSortChange={handleSortChangeScheduled}
              testSessions={scheduledTestSessionsQuery.data?.testSessions?.testSessions || []}
              pagination={scheduledTestSessionsQuery.data?.testSessions?.pagination}
              isLoading={scheduledTestSessionsQuery.loading}
              columnLabels={{
                status: null,
                invoiceCheck: null,
              }}
              onRowClick={({ id }) => history.push(`/${ROUTES.SESSIONS}/${id}`)}
            />
          </Box>
        )}
        {hasPermission([UserPermissions.SEE_PENDING_RESULTS_TEST_SESSIONS_DASHBOARD]) && (
          <Box minWidth={0}>
            <TestSessionsDatalist
              tableHeader={
                <Datalist.Title
                  rightContent={
                    <Button
                      size="medium"
                      colorVariant="secondary"
                      onClick={() => {
                        history.push({
                          pathname: `/${ROUTES.SESSIONS}`,
                          search: `?status=${TestSessionStatus.PendingResults}`,
                        })
                      }}
                    >
                      Show All
                    </Button>
                  }
                >
                  Pending Results Test Sessions
                </Datalist.Title>
              }
              testSessions={pendingResultsTestSessionsQuery.data?.testSessions?.testSessions || []}
              pagination={pendingResultsTestSessionsQuery.data?.testSessions?.pagination}
              columnLabels={{
                status: null,
                invoiceCheck: null,
              }}
              isLoading={pendingResultsTestSessionsQuery.loading}
              onRowClick={({ id }) => history.push(`/${ROUTES.SESSIONS}/${id}`)}
            />
          </Box>
        )}
        <Box minWidth={0}>
          {hasPermission([UserPermissions.SEE_FOLLOWUP_TEST_RESULT_DASHBOARD]) && (
            <TestsDatalist
              tests={testsQuery.data?.tests.tests || []}
              isLoading={testsQuery.loading}
              tableHeader={<Datalist.Title>Follow up test results</Datalist.Title>}
              onRowClick={({ id }) => history.push(`/${ROUTES.TESTS}/${id}`)}
            />
          )}
        </Box>
      </Grid>
    </DashboardPage>
  )
}
export default Dashboard
